import React, { useEffect, useMemo, useState } from "react";
import { FaThLarge, FaTable } from "react-icons/fa";

const STATUS = [
  "",
  "Licencié",
  "Licencié Hors Club",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

const FiltersBar = ({
  loading,
  dojos,
  selectedDojo,
  onDojoChange,
  groups,
  filterGroup,
  onGroupChange,
  seasons,
  selectedSeason,
  onSeasonChange,
  search,
  onSearchChange,
  statutInscription,
  onStatutChange,
  sortBy,
  onSortByChange,
  viewMode,
  onViewModeChange,
}) => {
  // debounce recherche
  const [localSearch, setLocalSearch] = useState(search || "");
  useEffect(() => setLocalSearch(search || ""), [search]);
  useEffect(() => {
    const t = setTimeout(() => onSearchChange(localSearch), 250);
    return () => clearTimeout(t);
  }, [localSearch, onSearchChange]);

  const hasSingleDojo = useMemo(() => (dojos || []).length === 1, [dojos]);
  const isTable = viewMode === "table";
  const toggleView = () => onViewModeChange(isTable ? "grid" : "table");

  const fieldClass =
    "select select-bordered w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 " +
    "border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/40";

  const inputClass =
    "input input-bordered w-full bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 " +
    "placeholder-slate-400 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/40";

  const labelClass =
    "block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1";

  const iconBtn =
    "p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition";

  return (
    <div className="space-y-3">
      {/* Ligne 1 : Filtres */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-5 items-end">
        {/* Saison */}
        <div>
          <label htmlFor="season" className={labelClass}>Saison</label>
          <select
            id="season"
            className={fieldClass}
            value={selectedSeason}
            onChange={(e) => onSeasonChange(e.target.value)}
            disabled={loading}
          >
            {(seasons || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Dojo (caché si 1 seul) */}
        {!hasSingleDojo && (
          <div>
            <label htmlFor="dojo" className={labelClass}>Dojo</label>
            <select
              id="dojo"
              className={fieldClass}
              value={selectedDojo}
              onChange={(e) => onDojoChange(e.target.value)}
              disabled={loading}
            >
              <option value="">Tous</option>
              {(dojos || []).map((d) => (
                <option key={d.id} value={String(d.id)}>{d.dojo_nom}</option>
              ))}
            </select>
          </div>
        )}

        {/* Groupe */}
        <div>
          <label htmlFor="group" className={labelClass}>Groupe</label>
          <select
            id="group"
            className={fieldClass}
            value={filterGroup}
            onChange={(e) => onGroupChange(e.target.value)}
            disabled={loading}
          >
            <option value="">— Sélectionner —</option>
            {(groups || []).map((g) => (
              <option key={g.id} value={g.nom}>{g.nom}</option>
            ))}
          </select>
        </div>

        {/* Statut */}
        <div>
          <label htmlFor="status" className={labelClass}>Statut</label>
          <select
            id="status"
            className={fieldClass}
            value={statutInscription}
            onChange={(e) => onStatutChange(e.target.value)}
          >
            {STATUS.map((s) => (
              <option key={s || "_"} value={s}>{s || "Tous"}</option>
            ))}
          </select>
        </div>

        {/* Tri */}
        <div>
          <label htmlFor="sort" className={labelClass}>Tri</label>
          <select
            id="sort"
            className={fieldClass}
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
          >
            <option value="alpha">Alphabétique</option>
            <option value="age-asc">Âge croissant</option>
            <option value="age-desc">Âge décroissant</option>
            <option value="statut">Par statut</option>
          </select>
        </div>

        {/* Switch Cartes ↔ Tableau */}
        <div className="md:self-end">
          <span className="sr-only" id="view-label">Vue</span>
          <div className="flex items-center justify-between md:justify-start gap-3" aria-labelledby="view-label">
            {/* Icône Cartes */}
            <button
              type="button"
              onClick={() => onViewModeChange("grid")}
              className={iconBtn}
              title="Cartes"
              aria-pressed={!isTable}
            >
              <FaThLarge
                className={isTable ? "text-slate-400" : "text-primary"}
                size={18}
              />
            </button>

            {/* Toggle style Dark/Light */}
            <button
              type="button"
              role="switch"
              aria-checked={isTable}
              onClick={toggleView}
              title={isTable ? "Passer en cartes" : "Passer en tableau"}
              className="relative inline-flex h-9 w-16 items-center rounded-full
                         bg-slate-200 dark:bg-slate-700 shadow-inner transition"
            >
              <span
                className={`absolute left-1 top-1 h-7 w-7 rounded-full bg-white shadow transition-transform ${
                  isTable ? "translate-x-7" : "translate-x-0"
                }`}
              />
              <span className="sr-only">Basculer la vue</span>
            </button>

            {/* Icône Tableau */}
            <button
              type="button"
              onClick={() => onViewModeChange("table")}
              className={iconBtn}
              title="Tableau"
              aria-pressed={isTable}
            >
              <FaTable
                className={isTable ? "text-primary" : "text-slate-400"}
                size={18}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Ligne 2 : Recherche */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">
        <div className="flex-1">
          <label htmlFor="search" className={labelClass}>Recherche</label>
          <input
            id="search"
            type="text"
            className={inputClass}
            placeholder="Nom ou prénom"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default FiltersBar;
